import {
  normalizeGiftPrograms,
  normalizeGiftRules,
  createGiftRetryController,
  reconcileGiftCart,
  resolveGiftVariantAvailability,
  scopeGiftPrograms,
  scopeGiftRules,
  subtotalWithoutGifts
} from './pttek-gift-cart.js';
import { setGiftRules } from './pttek-gift-rules-state.js';

export { createGiftRetryController, normalizeGiftRules, reconcileGiftCart, resolveGiftVariantAvailability, scopeGiftPrograms, scopeGiftRules };

const API_BASE = 'https://api.phuthaitech.com.vn';
const availabilityCache = new Map();
let programs = null;
let rules = null;
let syncing = false;
let latestCart = null;
let rulesPublished = false;
const retry = createGiftRetryController({
  onRetry: async () => {
    try {
      latestCart = await getCart();
      await reconcile(latestCart);
    } catch (error) {
      console.warn('Gift retry unavailable', error);
    }
  }
});

const parseJson = (node, fallback = {}) => {
  try { return node ? JSON.parse(node.textContent) : fallback; } catch { return fallback; }
};
const config = () => ({
  ...parseJson(document.querySelector('[data-cart-page-config]')),
  ...parseJson(document.querySelector('[data-pttek-cart-config]'))
});
const campaignConfig = () => parseJson(document.querySelector('[data-gift-campaign-config]'), {
  campaignRunning: false, selectedRuleIds: []
});
const money = (amount, options) => new Intl.NumberFormat(options.locale || 'vi', {
  style: 'currency', currency: options.currency || 'VND', maximumFractionDigits: 0
}).format(amount);

const renderProgress = (cart) => {
  const subtotal = subtotalWithoutGifts(cart);
  const options = config();
  document.querySelectorAll('[data-gift-progress]').forEach((root) => {
    root.replaceChildren();
    (rules || []).forEach((rule) => {
      const remaining = Math.max(0, rule.threshold - subtotal);
      const item = document.createElement('div');
      const copy = document.createElement('p');
      copy.textContent = String(remaining === 0 ? root.dataset.giftMet : root.dataset.giftRemaining)
        .replace('__TITLE__', rule.title).replace('__AMOUNT__', money(remaining, options));
      const track = document.createElement('span');
      const fill = document.createElement('i');
      fill.style.inlineSize = `${Math.min(100, Math.round((subtotal / rule.threshold) * 100))}%`;
      track.append(fill);
      item.append(copy, track);
      root.append(item);
    });
    root.hidden = !rules?.length;
  });
};

const selectedIds = (root) => {
  const script = root.parentElement?.querySelector('[data-campaign-gift-rule-ids]');
  const ids = parseJson(script, []);
  return Array.isArray(ids) ? ids : [];
};
const renderCampaignRules = () => document.querySelectorAll('[data-campaign-gift-rules]').forEach((root) => {
  root.replaceChildren();
  const selected = scopeGiftPrograms(programs || [], { campaignRunning: true, selectedRuleIds: selectedIds(root) });
  selected.forEach((rule) => {
    const item = document.createElement('p');
    const detail = rule.kind === 'gift_threshold'
      ? String(root.dataset.thresholdTemplate || '').replace('__AMOUNT__', money(rule.threshold, config()))
      : String(root.dataset.bxgyTemplate || '').replace('__BUY__', String(rule.buyQuantity)).replace('__GET__', String(rule.getQuantity));
    const title = document.createElement('strong');
    title.textContent = rule.title;
    item.append(title, document.createTextNode(` · ${detail}`));
    root.append(item);
  });
  root.hidden = selected.length === 0;
});

const sectionRequest = () => {
  const main = document.querySelector('[data-main-cart]');
  return main ? { sections: main.dataset.sectionId, sections_url: config().cartUrl || '/cart' } : {};
};
const request = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST', credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, ...sectionRequest() })
  });
  if (!response.ok) throw new Error(`Gift cart request failed: ${response.status}`);
  return response.json();
};
const refreshCart = async () => {
  const options = config();
  if (document.querySelector('[data-main-cart]')) return request(options.updateUrl || '/cart/update.js', { updates: {} });
  const response = await fetch(`${options.cartUrl || '/cart'}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
  return response.json();
};
const getCart = async () => {
  const response = await fetch(`${config().cartUrl || '/cart'}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
};
const announce = (cart) => window.dispatchEvent(new CustomEvent('pttek:cart:render', { detail: { cart } }));

const reconcile = async (cart) => {
  if (syncing || !rules) return false;
  syncing = true;
  try {
    const options = config();
    const mutated = await reconcileGiftCart(cart, rules, {
      request,
      addUrl: options.addUrl || '/cart/add.js',
      changeUrl: options.changeUrl || '/cart/change.js',
      retry,
      availability: (rule) => resolveGiftVariantAvailability(rule, fetch, availabilityCache)
    });
    if (!mutated) return true;
    const refreshed = await refreshCart();
    latestCart = refreshed;
    renderProgress(refreshed);
    announce(refreshed);
    return true;
  } catch (error) {
    console.warn('Gift cart sync unavailable', error);
    return false;
  } finally {
    syncing = false;
  }
};

export const publishGiftRulesAfterReconciliation = async (nextRules, reconciliation, publish = setGiftRules) => {
  if (!await reconciliation()) return false;
  publish(nextRules);
  return true;
};

const publishRulesAfterReconcile = async (cart) => {
  if (!rulesPublished && await publishGiftRulesAfterReconciliation(rules, () => reconcile(cart))) {
    rulesPublished = true;
  }
};

const loadRules = async () => {
  try {
    const response = await fetch(`${API_BASE}/mobile/gifts/active`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Gift rules request failed: ${response.status}`);
    programs = normalizeGiftPrograms(await response.json());
    rules = scopeGiftRules(programs, campaignConfig());
    rulesPublished = false;
    renderCampaignRules();
    if (latestCart) { renderProgress(latestCart); await publishRulesAfterReconcile(latestCart); }
  } catch (error) {
    rulesPublished = false;
    setGiftRules([]);
    document.querySelectorAll('[data-gift-progress], [data-campaign-gift-rules]').forEach((root) => { root.hidden = true; });
    console.warn('Gift rules unavailable', error);
  }
};

const init = async () => {
  window.addEventListener('pttek:cart:rendered', (event) => {
    if (!event.detail?.cart) return;
    latestCart = event.detail.cart;
    renderProgress(latestCart);
    void publishRulesAfterReconcile(latestCart);
  });
  await loadRules();
  if (rules && !latestCart && document.querySelector('[data-gift-progress]')) {
    try {
      latestCart = await getCart();
      renderProgress(latestCart);
      await publishRulesAfterReconcile(latestCart);
    } catch (error) {
      console.warn('Gift cart state unavailable', error);
    }
  }
};
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else void init();
}
